import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  EVENT_STORAGE_KEY,
  createBaselineEventState,
  normalizeCommunityCupDivisionData,
  normalizeEventState,
  normalizeScheduleConfig,
} from "@/lib/event-state";
import { EventState } from "@/types/tournament";
import {
  EventFormat,
  OrganizerEvent,
  OrganizerEventMeta,
  OrganizerEventsSnapshot,
  OrganizerEventStatus,
  OrganizerWorkspaceMode,
} from "@/types/events";
import { useAuth } from "@/context/AuthContext";
import {
  deleteEventFromCloud,
  loadEventsFromCloud,
  upsertEventToCloud,
} from "@/lib/services/organizer-sync";

const ORGANIZER_EVENTS_STORAGE_KEY = "ccb-organizer-events-store-v1";
const LAST_ORGANIZER_USER_KEY = "ccb-last-organizer-user-v1";

const defaultFormat: EventFormat = "bracket";
const validFormats: EventFormat[] = ["bracket", "group-knockout", "americano", "mexicano", "braziliano", "swiss", "king", "beat-the-box", "banded-mexicano", "community-cup", "builder"];
const validStatuses: OrganizerEventStatus[] = ["draft", "live", "finished", "archived"];

const KING_FORMAT_ALIASES = new Set(["king", "king-of-the-court", "king_of_the_court", "kings-court", "kingcourt"]);

const normalizeEventFormat = (rawFormat?: string | null): EventFormat => {
  if (!rawFormat) return defaultFormat;
  const normalized = rawFormat.toLowerCase();
  if (KING_FORMAT_ALIASES.has(normalized)) {
    return "king";
  }
  return validFormats.includes(rawFormat as EventFormat) ? (rawFormat as EventFormat) : defaultFormat;
};

const createEventSlug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "") || "event";

const generateEventId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `event-${Date.now().toString(36)}`;
};

const ensureUniqueSlug = (slug: string, events: Record<string, OrganizerEvent>, excludeId?: string) => {
  const used = new Set(Object.values(events).filter((e) => e.id !== excludeId).map((e) => e.slug));
  if (!used.has(slug)) return slug;
  let suffix = 2;
  let candidate = `${slug}-${suffix}`;
  while (used.has(candidate)) {
    suffix += 1;
    candidate = `${slug}-${suffix}`;
  }
  return candidate;
};

const withMeta = (
  base: EventState,
  meta?: Partial<OrganizerEventMeta>,
  events: Record<string, OrganizerEvent> = {}
): OrganizerEvent => {
  const now = new Date().toISOString();
  const id = meta?.id ?? generateEventId();
  const baseSlug = meta?.slug ?? createEventSlug(base.eventName);
  const slug = ensureUniqueSlug(baseSlug, events);
  const format = normalizeEventFormat(meta?.format);
  const isFixedPartner = Boolean(meta?.isFixedPartner);
  const status = validStatuses.includes(meta?.status as OrganizerEventStatus)
    ? (meta?.status as OrganizerEventStatus)
    : "draft";
  const startDate = meta?.startDate ?? null;
  const endDate = meta?.endDate ?? (startDate ?? null);
  const workspaceMode: OrganizerWorkspaceMode = meta?.workspaceMode === "quick" ? "quick" : "full";
  return {
    ...base,
    id,
    slug,
    format,
    isFixedPartner,
    status,
    createdAt: meta?.createdAt ?? now,
    updatedAt: meta?.updatedAt ?? now,
    publishSchedule: meta?.publishSchedule ?? false,
    publishBracket: meta?.publishBracket ?? false,
    displayEnabled: meta?.displayEnabled ?? false,
    publishingWorkspaceEnabled: meta?.publishingWorkspaceEnabled ?? false,
    extrasWorkspaceEnabled: meta?.extrasWorkspaceEnabled ?? false,
    startDate,
    endDate,
    workspaceMode,
    scheduleConfig: normalizeScheduleConfig(base.scheduleConfig),
  };
};

// Collapses slugs corrupted by a uniqueness-check bug (e.g. "foo-2-2-2-2" → "foo").
const repairSlug = (slug: string): string => slug.replace(/(-\d+)\1+$/, "");

// Exported so the Community Cup normalization wiring can be verified
// directly by a unit test (see
// src/context/__tests__/OrganizerEventsContext.communityCup.test.ts)
// without needing to exercise the whole provider/localStorage/cloud-sync
// stack.
export const sanitizeEvent = (event: OrganizerEvent): OrganizerEvent => {
  const format = normalizeEventFormat(event.format);
  const status = validStatuses.includes(event.status) ? event.status : "draft";
  const startDate = event.startDate ?? null;
  const endDate = event.endDate ?? startDate;
  const workspaceMode: OrganizerWorkspaceMode = event.workspaceMode === "quick" ? "quick" : "full";
  // Migrates legacy top-level `communityCup.{pairsA,pairsB,fixtures,
  // jokerWindows,roundTimes,savedSchedules,substitutions}` (pre-multi-
  // division shape) into the event's sole division's `communityCup` field.
  // This is the load-bearing hydration call site: every event loaded from
  // ORGANIZER_EVENTS_STORAGE_KEY (buildInitialSnapshot) and every cloud
  // event merged in (mergeCloudEvents / buildCloudOnlySnapshot) goes
  // through sanitizeEvent, so this runs before any page reads
  // `communityCup` off an event or its divisions.
  const { communityCup, divisions } = normalizeCommunityCupDivisionData(event);
  return {
    ...event,
    slug: repairSlug(event.slug),
    format,
    isFixedPartner: Boolean((event as OrganizerEvent).isFixedPartner),
    status,
    startDate,
    endDate,
    workspaceMode,
    publishingWorkspaceEnabled: typeof event.publishingWorkspaceEnabled === "boolean" ? event.publishingWorkspaceEnabled : true,
    extrasWorkspaceEnabled: typeof event.extrasWorkspaceEnabled === "boolean" ? event.extrasWorkspaceEnabled : true,
    scheduleConfig: normalizeScheduleConfig((event as EventState).scheduleConfig),
    communityCup,
    divisions,
  };
};

const buildInitialSnapshot = (): OrganizerEventsSnapshot => {
  const fallback = () => {
    const base = createBaselineEventState();
    const event = withMeta(base);
    return {
      events: { [event.id]: event },
      activeEventId: event.id,
    };
  };

  if (typeof window === "undefined") {
    return fallback();
  }

  try {
    const stored = window.localStorage.getItem(ORGANIZER_EVENTS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as OrganizerEventsSnapshot;
      if (parsed && parsed.events && typeof parsed.events === "object") {
        const events: Record<string, OrganizerEvent> = {};
        Object.values(parsed.events).forEach((event) => {
          if (event && typeof event === "object" && "id" in event) {
            const typed = sanitizeEvent(event as OrganizerEvent);
            events[typed.id] = typed;
          }
        });
        const activeId = parsed.activeEventId && events[parsed.activeEventId]
          ? parsed.activeEventId
          : Object.keys(events)[0];
        if (Object.keys(events).length > 0 && activeId) {
          return {
            events,
            activeEventId: activeId,
          };
        }
      }
    }

    const legacy = window.localStorage.getItem(EVENT_STORAGE_KEY);
    if (legacy) {
      const parsedLegacy = JSON.parse(legacy);
      const normalized = normalizeEventState(parsedLegacy);
      if (normalized) {
        // Routed through sanitizeEvent (not just withMeta) so the Community
        // Cup legacy-shape normalizer runs here too — this is the other,
        // older hydration path (pre-`ORGANIZER_EVENTS_STORAGE_KEY`) that used
        // to skip it entirely (final review, minor item under C2).
        const event = sanitizeEvent(withMeta(normalized));
        window.localStorage.removeItem(EVENT_STORAGE_KEY);
        return {
          events: { [event.id]: event },
          activeEventId: event.id,
        };
      }
    }
  } catch (error) {
    console.warn("Failed to load organizer events from storage", error);
  }

  return fallback();
};

/** Merge cloud events into local snapshot. Cloud takes precedence for same IDs. */
const mergeCloudEvents = (
  local: OrganizerEventsSnapshot,
  cloudEvents: OrganizerEvent[]
): OrganizerEventsSnapshot => {
  if (cloudEvents.length === 0) return local;

  const cloudIds = new Set(cloudEvents.map((e) => e.id));
  const merged: Record<string, OrganizerEvent> = { ...local.events };
  for (const cloudEvent of cloudEvents) {
    const sanitized = sanitizeEvent(cloudEvent);
    const existing = merged[sanitized.id];
    // Cloud wins if it's newer or there's no local copy
    if (!existing || sanitized.updatedAt > existing.updatedAt) {
      merged[sanitized.id] = sanitized;
    }
  }

  // Prefer a cloud-backed event as the active event so a local placeholder
  // doesn't steal focus over the user's real events.
  const localActiveIsCloudBacked = local.activeEventId && cloudIds.has(local.activeEventId);
  const activeId = localActiveIsCloudBacked
    ? local.activeEventId
    : (cloudEvents[0]?.id ?? Object.keys(merged)[0] ?? null);

  return { events: merged, activeEventId: activeId };
};

/** Build a snapshot from cloud events only — discards any local state. */
const buildCloudOnlySnapshot = (cloudEvents: OrganizerEvent[]): OrganizerEventsSnapshot => {
  const events: Record<string, OrganizerEvent> = {};
  for (const cloudEvent of cloudEvents) {
    const sanitized = sanitizeEvent(cloudEvent);
    events[sanitized.id] = sanitized;
  }
  const firstId = Object.keys(events)[0] ?? null;
  return { events, activeEventId: firstId };
};

interface OrganizerEventsContextValue {
  snapshot: OrganizerEventsSnapshot;
  activeEvent: OrganizerEvent;
  isCloudSyncing: boolean;
  /** Number of events loaded from cloud on last login. Used by migration banner. */
  cloudEventsLoaded: number;
  setActiveEventId: (eventId: string) => void;
  setActiveEventBySlug: (slug: string) => OrganizerEvent | null;
  getEventBySlug: (slug: string) => OrganizerEvent | null;
  mutateActiveEvent: (updater: (current: OrganizerEvent) => OrganizerEvent) => void;
  upsertEvent: (event: OrganizerEvent) => void;
  applyCloudEventUpdate: (event: OrganizerEvent) => void;
  createEventFromState: (event: EventState, meta?: Partial<OrganizerEventMeta>) => OrganizerEvent;
  deleteEvent: (eventId: string) => void;
}

const OrganizerEventsContext = createContext<OrganizerEventsContextValue | undefined>(undefined);

export const OrganizerEventsProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  // Track whether the initial snapshot came from localStorage or was auto-generated.
  // If auto-generated (no prior local data), discard it when cloud events load
  // so we don't show a blank placeholder alongside real cloud events.
  const isInitialSnapshotFallback = useRef(
    typeof window === "undefined" ||
    (!window.localStorage.getItem(ORGANIZER_EVENTS_STORAGE_KEY) &&
     !window.localStorage.getItem(EVENT_STORAGE_KEY))
  );
  const [snapshot, setSnapshot] = useState<OrganizerEventsSnapshot>(() => buildInitialSnapshot());
  const [isCloudSyncing, setIsCloudSyncing] = useState(false);
  const [cloudEventsLoaded, setCloudEventsLoaded] = useState(0);

  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Only sync events that were actually mutated locally.
  const pendingUpsertsRef = useRef<Set<string>>(new Set());
  const pendingDeletesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    setSnapshot((current) => {
      if (Object.keys(current.events).length > 0 && current.activeEventId && current.events[current.activeEventId]) {
        return current;
      }
      const entries = Object.entries(current.events);
      if (entries.length === 0) {
        const base = createBaselineEventState();
        const event = withMeta(base);
        return { events: { [event.id]: event }, activeEventId: event.id };
      }
      const [firstId, firstEvent] = entries[0];
      return { events: { ...current.events, [firstId]: firstEvent }, activeEventId: firstId };
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(ORGANIZER_EVENTS_STORAGE_KEY, JSON.stringify(snapshot));
    } catch (error) {
      console.warn("Failed to persist organizer events", error);
    }
  }, [snapshot]);

  useEffect(() => {
    if (!user) return;

    // Detect when a different user logs in on the same device to prevent
    // the previous user's cached events from leaking into this session.
    const lastUserId = typeof window !== "undefined"
      ? window.localStorage.getItem(LAST_ORGANIZER_USER_KEY)
      : null;
    const isUserSwitch = lastUserId !== null && lastUserId !== user.id;
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LAST_ORGANIZER_USER_KEY, user.id);
    }
    if (isUserSwitch) {
      // Wipe local cache so User A's events don't bleed into User B's session.
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(ORGANIZER_EVENTS_STORAGE_KEY);
      }
      isInitialSnapshotFallback.current = true;
    }

    setIsCloudSyncing(true);
    loadEventsFromCloud().then((cloudEvents) => {
      setCloudEventsLoaded(cloudEvents.length);
      if (cloudEvents.length > 0) {
        if (isInitialSnapshotFallback.current) {
          // No prior local data (or user switch) — replace the fallback entirely
          // so real cloud events are shown without a spurious blank draft.
          isInitialSnapshotFallback.current = false;
          setSnapshot(buildCloudOnlySnapshot(cloudEvents));
        } else {
          setSnapshot((current) => mergeCloudEvents(current, cloudEvents));
        }
      } else if (isUserSwitch) {
        // User switch with no cloud events — start with a blank draft.
        const base = createBaselineEventState();
        const event = withMeta(base);
        setSnapshot({ events: { [event.id]: event }, activeEventId: event.id });
      }
    }).finally(() => {
      setIsCloudSyncing(false);
    });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced cloud sync — only uploads events that were mutated locally.
  useEffect(() => {
    if (!user) return;

    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      // pendingUpsertsRef/pendingDeletesRef ARE the "is there a local write
      // to upload" signal — they're only ever populated by the local
      // mutation functions (mutateActiveEvent/upsertEvent/deleteEvent),
      // never by the cloud-load path. So a cloud-load-only cycle naturally
      // has both sets empty — toUpsert/toDelete below are empty arrays,
      // Promise.all([]) resolves immediately, and no network call is made.
      void (async () => {
        const toUpsert = [...pendingUpsertsRef.current];
        const toDelete = [...pendingDeletesRef.current];
        pendingUpsertsRef.current.clear();
        pendingDeletesRef.current.clear();

        const [upsertResults, deleteResults] = await Promise.all([
          Promise.all(
            toUpsert.map(async (id) => {
              const event = snapshot.events[id];
              // Event no longer exists locally (e.g. it was deleted before this
              // cycle fired) — nothing to upload, so nothing to retry either.
              const ok = event ? await upsertEventToCloud(event, user.id) : true;
              return { id, ok };
            })
          ),
          Promise.all(
            toDelete.map(async (id) => ({ id, ok: await deleteEventFromCloud(id) }))
          ),
        ]);

        // A failed write (network/RLS error) re-queues its id so the NEXT
        // debounce cycle — triggered by a subsequent snapshot change — retries
        // it. This is naturally throttled: there's no self-scheduling timer
        // here, so a persistent failure doesn't hot-loop, it just waits for
        // the next real mutation (or cloud-load snapshot update) to try again.
        upsertResults.forEach(({ id, ok }) => {
          if (!ok) pendingUpsertsRef.current.add(id);
        });
        deleteResults.forEach(({ id, ok }) => {
          if (!ok) pendingDeletesRef.current.add(id);
        });
      })();
    }, 1000);

    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [snapshot, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeEvent = useMemo(() => {
    if (!snapshot.activeEventId) return null;
    return snapshot.events[snapshot.activeEventId] ?? null;
  }, [snapshot.activeEventId, snapshot.events]);

  const setActiveEventId = useCallback((eventId: string) => {
    setSnapshot((current) => {
      if (!current.events[eventId]) return current;
      if (current.activeEventId === eventId) return current;
      return {
        ...current,
        activeEventId: eventId,
      };
    });
  }, []);

  const setActiveEventBySlug = useCallback((slug: string) => {
    let found: OrganizerEvent | null = null;
    setSnapshot((current) => {
      const target = Object.values(current.events).find((e) => e.slug === slug);
      if (!target) return current;
      found = target;
      if (current.activeEventId === target.id) return current;
      return { ...current, activeEventId: target.id };
    });
    return found;
  }, []);

  const getEventBySlug = useCallback(
    (slug: string) => {
      if (!slug) return null;
      return Object.values(snapshot.events).find((event) => event.slug === slug) ?? null;
    },
    [snapshot.events]
  );

  const mutateActiveEvent = useCallback((updater: (current: OrganizerEvent) => OrganizerEvent) => {
    setSnapshot((current) => {
      const activeId = current.activeEventId;
      if (!activeId) return current;
      const existing = current.events[activeId];
      if (!existing) return current;
      const updated = sanitizeEvent(updater(existing));
      if (updated === existing) return current;
      pendingUpsertsRef.current.add(activeId);
      return {
        ...current,
        events: {
          ...current.events,
          [activeId]: { ...updated, updatedAt: new Date().toISOString() },
        },
      };
    });
  }, []);

  const upsertEvent = useCallback((event: OrganizerEvent) => {
    pendingUpsertsRef.current.add(event.id);
    setSnapshot((current) => {
      const normalized = sanitizeEvent(event);
      const nextEvents = {
        ...current.events,
        [normalized.id]: {
          ...normalized,
          slug: ensureUniqueSlug(normalized.slug, current.events, normalized.id),
          updatedAt: new Date().toISOString(),
        },
      };
      const activeId = current.activeEventId && nextEvents[current.activeEventId]
        ? current.activeEventId
        : normalized.id;
      return { events: nextEvents, activeEventId: activeId };
    });
  }, []);

  /**
   * Merges a cloud-CONFIRMED event (the return value of a targeted,
   * CAS-guarded write that already landed in the database — e.g.
   * `saveDivisionRoster`/`patchDivisionCommunityCupForOrganizer` in
   * `src/lib/services/communityCup.ts`, used by `CommunityCupRosterPage.tsx`
   * and `CommunityCupSchedulePage.tsx`) into the local snapshot, WITHOUT
   * queuing it for the debounced sync below.
   *
   * That omission is deliberate, not an oversight: `upsertEvent` marks its
   * event dirty for the next sync cycle, which uploads the entire local
   * `state` blob unconditionally (`upsertEventToCloud` — no CAS, no merge).
   * Re-arming that blind overwrite one second later — with, by then,
   * potentially-stale local data again — would silently reopen the exact
   * race those targeted writes exist to close. This only updates what pages
   * read locally (so switching divisions and back, or another page reading
   * `activeEvent`, doesn't show/re-save stale data) — the write itself is
   * already durable.
   */
  const applyCloudEventUpdate = useCallback((event: OrganizerEvent) => {
    setSnapshot((current) => {
      if (!current.events[event.id]) return current;
      const sanitized = sanitizeEvent(event);
      return {
        ...current,
        events: { ...current.events, [event.id]: sanitized },
      };
    });
  }, []);

  const createEventFromState = useCallback(
    (eventState: EventState, meta?: Partial<OrganizerEventMeta>) => {
      const normalizedMeta: Partial<OrganizerEventMeta> | undefined = meta
        ? {
            ...meta,
            isFixedPartner: Boolean(meta.isFixedPartner),
            startDate: meta.startDate ?? null,
            endDate: meta.endDate ?? (meta.startDate ?? null),
            workspaceMode: meta.workspaceMode === "quick" ? "quick" : "full",
          }
        : undefined;
      const event = withMeta(eventState, normalizedMeta, snapshot.events);
      upsertEvent(event);
      return event;
    },
    [snapshot.events, upsertEvent]
  );

  const deleteEvent = useCallback((eventId: string) => {
    pendingDeletesRef.current.add(eventId);
    setSnapshot((current) => {
      if (!current.events[eventId]) return current;
      const nextEvents = { ...current.events };
      delete nextEvents[eventId];
      const remainingIds = Object.keys(nextEvents);
      const nextActive =
        current.activeEventId === eventId
          ? remainingIds[0] ?? null
          : current.activeEventId;
      if (!nextActive && remainingIds.length === 0) {
        const base = createBaselineEventState();
        const fallbackEvent = withMeta(base);
        return {
          events: { [fallbackEvent.id]: fallbackEvent },
          activeEventId: fallbackEvent.id,
        };
      }
      return {
        events: nextEvents,
        activeEventId: nextActive,
      };
    });
  }, []);

  const value = useMemo(() => {
    let ensuredActive = activeEvent;
    if (!ensuredActive) {
      const base = createBaselineEventState();
      ensuredActive = withMeta(base);
    }
    return {
      snapshot,
      activeEvent: ensuredActive,
      isCloudSyncing,
      cloudEventsLoaded,
      setActiveEventId,
      setActiveEventBySlug,
      getEventBySlug,
      mutateActiveEvent,
      upsertEvent,
      applyCloudEventUpdate,
      createEventFromState,
      deleteEvent,
    };
  }, [
    activeEvent,
    snapshot,
    isCloudSyncing,
    cloudEventsLoaded,
    setActiveEventId,
    setActiveEventBySlug,
    getEventBySlug,
    mutateActiveEvent,
    upsertEvent,
    applyCloudEventUpdate,
    createEventFromState,
    deleteEvent,
  ]);

  return (
    <OrganizerEventsContext.Provider value={value}>
      {children}
    </OrganizerEventsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOrganizerEvents = () => {
  const context = useContext(OrganizerEventsContext);
  if (!context) {
    throw new Error("useOrganizerEvents must be used within an OrganizerEventsProvider");
  }
  return context;
};
